import { Injectable, computed, signal } from '@angular/core';
import { normalizeMerchantAccounts } from '@shared/utils/merchant-account.util';
import type { MerchantAccount, RawMerchantAccount } from '../models';

/**
 * How long a half-finished login may sit in memory before it is thrown away.
 *
 * Agreed at 10 minutes (decision D6). Long enough to survive a real interruption
 * mid-login, short enough that an abandoned tab is not holding a password overnight.
 */
export const PENDING_LOGIN_TTL_MS = 10 * 60 * 1000;

/**
 * Thrown when an account is chosen after the pending login has already been discarded —
 * the tab sat on the picker past `PENDING_LOGIN_TTL_MS`, or the store was cleared.
 *
 * A dedicated type so the picker can show "your session expired, sign in again" instead of
 * a generic failure, without inspecting message strings.
 */
export class PendingLoginExpiredError extends Error {
  constructor() {
    super('Pending login expired');
    this.name = 'PendingLoginExpiredError';
  }
}

interface PendingLogin {
  /** What the user typed on the login screen — an email today, a phone eventually. */
  username: string;
  /** The password, replayed to `/account/session`. See the class comment. */
  credential: string;
  accounts: MerchantAccount[];
  createdAt: number;
}

/**
 * State for a login that authenticated but has not yet been scoped to a merchant.
 *
 * ## Why a raw password lives here
 *
 * When a user belongs to several merchants the backend accepts the credential but issues
 * **no token**, returning the account list instead. The follow-up `/account/session` call is
 * stateless — nothing carries "this person already proved themselves" forward — so the
 * credential itself is the only proof the client can present a second time.
 *
 * Consequences, all deliberate:
 *
 * - **Memory only.** Never `localStorage`, never `sessionStorage`, never a cookie. A page
 *   refresh or tab close must lose it; `accountSelectionGuard` then sends the user back to
 *   `/login`, which is the correct outcome rather than a bug.
 * - **Time-bounded.** See `PENDING_LOGIN_TTL_MS`.
 * - **Cleared eagerly** — on success, on logout, on leaving the picker, on expiry.
 *
 * The proper fix is a short-lived one-time `login_challenge` token from the backend, which
 * would remove the credential from the browser entirely. Raised with backend; not blocking.
 */
@Injectable({ providedIn: 'root' })
export class PendingLoginStore {
  private readonly state = signal<PendingLogin | null>(null);

  /** The merchants to choose between. Empty when no login is pending. */
  readonly accounts = computed<MerchantAccount[]>(() => this.state()?.accounts ?? []);

  /**
   * Whether a pending login exists at all, ignoring age.
   *
   * Freshness is deliberately *not* part of this signal: it would depend on the wall clock,
   * which is not reactive, so the computed would go stale rather than expire. Callers that
   * care about age use `isFresh()`.
   */
  readonly hasPending = computed(() => this.state() !== null);

  /**
   * Record a login awaiting merchant selection.
   *
   * Accounts are normalized on the way in for the same reason as every other store — this
   * is a boundary against raw backend JSON.
   */
  start(params: { username: string; credential: string; accounts: RawMerchantAccount[] }): void {
    this.state.set({
      username: params.username,
      credential: params.credential,
      accounts: normalizeMerchantAccounts(params.accounts),
      createdAt: Date.now(),
    });
  }

  /**
   * The pending login, or `null` if there is none or it has expired.
   *
   * A method rather than a signal: the answer changes with the passage of time, not with a
   * state write, so a `computed` would cache a stale `false` for `isExpired`.
   */
  snapshot(): PendingLogin | null {
    const pending = this.state();
    if (!pending) return null;
    return this.isFresh() ? pending : null;
  }

  /** True when a pending login exists and is still inside its TTL. */
  isFresh(): boolean {
    const pending = this.state();
    if (!pending) return false;
    return Date.now() - pending.createdAt < PENDING_LOGIN_TTL_MS;
  }

  /**
   * True when a login was started but has since timed out.
   *
   * Distinct from `!isFresh()`: that is also true when nothing was ever started, which
   * should send the user to `/login` silently rather than claim their session expired.
   */
  isExpired(): boolean {
    return this.state() !== null && !this.isFresh();
  }

  /** Drop the credential and the account list. Idempotent. */
  clear(): void {
    this.state.set(null);
  }
}
